import type { CSSProperties } from "react";
import {
  type ViewportBgLiveEffect,
  type ViewportBgPreset,
  viewportBgCssBackground,
} from "./viewportBgPresets.ts";

interface ViewportBackgroundProps {
  preset: ViewportBgPreset;
}

interface Star {
  id: string;
  x: number;
  y: number;
  size: number;
  opacity: number;
  twinkle: boolean;
  duration: number;
  delay: number;
}

type StarStyle = CSSProperties & {
  "--star-dim": string;
  "--star-bright": string;
};

const STAR_COUNT = 64;

const VIEWPORT_BACKGROUND_STYLES = `
@keyframes poserefViewportStarTwinkle {
  0%, 100% {
    opacity: var(--star-dim);
    transform: scale(0.72);
  }
  48%, 58% {
    opacity: var(--star-bright);
    transform: scale(1.28);
  }
}

.poseref-viewport-star {
  position: absolute;
  border-radius: 999px;
  background: radial-gradient(
    circle,
    rgba(245, 250, 255, 0.96) 0%,
    rgba(245, 250, 255, 0.82) 28%,
    rgba(245, 250, 255, 0.18) 56%,
    rgba(245, 250, 255, 0) 72%
  );
  transform: translateZ(0);
  will-change: opacity, transform;
}

@media (prefers-reduced-motion: reduce) {
  .poseref-viewport-star {
    animation: none !important;
  }
}
`;

function seededRandom(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value = (value * 1664525 + 1013904223) >>> 0;
    return value / 0x100000000;
  };
}

function createStarField(): Star[] {
  const random = seededRandom(0x57a9_2c31);
  return Array.from({ length: STAR_COUNT }, (_, index) => {
    const twinkle = index % 4 === 0 || (index > 18 && random() > 0.78);
    const size = twinkle ? 3.2 + random() * 2.4 : 2.4 + random() * 1.6;
    const x = 2 + random() * 96;
    const y = 3 + random() * 83;
    return {
      id: `star-${x.toFixed(2)}-${y.toFixed(2)}`,
      x,
      y,
      size,
      opacity: twinkle ? 0.32 + random() * 0.22 : 0.18 + random() * 0.22,
      twinkle,
      duration: 4.8 + random() * 5.6,
      delay: -random() * 8,
    };
  });
}

const STARRY_NIGHT_STARS = createStarField();

function StarryNightEffect() {
  return (
    <>
      <style>{VIEWPORT_BACKGROUND_STYLES}</style>
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        {STARRY_NIGHT_STARS.map((star) => {
          const bright = Math.min(0.95, star.opacity + 0.5);
          const style: StarStyle = {
            "--star-dim": String(star.opacity),
            "--star-bright": String(bright),
            left: `${star.x}%`,
            top: `${star.y}%`,
            width: star.size,
            height: star.size,
            opacity: star.opacity,
            animation: star.twinkle
              ? `poserefViewportStarTwinkle ${star.duration}s ease-in-out ${star.delay}s infinite`
              : undefined,
          };
          return (
            <span
              key={star.id}
              className="poseref-viewport-star"
              style={style}
            />
          );
        })}
      </div>
    </>
  );
}

function renderLiveEffect(effect?: ViewportBgLiveEffect) {
  if (!effect) return null;
  switch (effect.kind) {
    case "starryNight":
      return <StarryNightEffect />;
  }
}

export function ViewportBackground({ preset }: ViewportBackgroundProps) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        overflow: "hidden",
        pointerEvents: "none",
        background: viewportBgCssBackground(preset),
      }}
    >
      {renderLiveEffect(preset.liveEffect)}
    </div>
  );
}
